import type { DocumentCategory, DocumentType } from '../../../core/types/data-models';

// Which of the document-type catalog a project can generate next, per
// category — the logic behind Next Steps and the AI Assistants roster.

/** Active root document types not generated yet whose prerequisites are all met — in category, then `order`, order. */
export function buildNextSteps(
  docTypes: DocumentType[],
  categories: DocumentCategory[],
  existingDocumentTypes: Set<string>,
): { docType: DocumentType; category: DocumentCategory | undefined }[] {
  const categoryRank = new Map(categories.map((c, i) => [c.type, i]));
  const categoryMap = new Map(categories.map((c) => [c.type, c]));
  return docTypes
    .filter(
      (dt) =>
        dt.status === 'Active' &&
        dt.parentDocumentType === null &&
        !existingDocumentTypes.has(dt.type) &&
        dt.dependentDocumentTypes.every((dep) => existingDocumentTypes.has(dep)),
    )
    .sort((a, b) => {
      const ra = categoryRank.get(a.categoryType) ?? 999;
      const rb = categoryRank.get(b.categoryType) ?? 999;
      if (ra !== rb) return ra - rb;
      return (a.order ?? 999) - (b.order ?? 999);
    })
    .map((dt) => ({ docType: dt, category: categoryMap.get(dt.categoryType) }));
}

/** A category's not-yet-generatable root document type, plus the prerequisite
 * document types still missing before it can be generated. */
export type BlockedNextDoc = { docType: DocumentType; missingDeps: DocumentType[] };

/**
 * For each category with no generatable document type yet (i.e. every root
 * doc type in it is still waiting on a dependency from elsewhere), picks the
 * one closest to being unlocked — fewest missing prerequisites, ties broken
 * by `order` — so the UI can tell the user what to generate first instead of
 * just "no documents yet".
 */
export function buildBlockedByCategory(
  docTypes: DocumentType[],
  existingDocumentTypes: Set<string>,
): Map<string, BlockedNextDoc> {
  const docTypeByType = new Map(docTypes.map((dt) => [dt.type, dt]));
  const result = new Map<string, BlockedNextDoc>();
  for (const dt of docTypes) {
    if (dt.status !== 'Active' || dt.parentDocumentType !== null) continue;
    if (existingDocumentTypes.has(dt.type)) continue;
    const missingDeps = dt.dependentDocumentTypes
      .filter((dep) => !existingDocumentTypes.has(dep))
      .map((dep) => docTypeByType.get(dep))
      .filter((dep): dep is DocumentType => dep != null);
    if (missingDeps.length === 0) continue;

    const current = result.get(dt.categoryType);
    if (
      !current ||
      missingDeps.length < current.missingDeps.length ||
      (missingDeps.length === current.missingDeps.length && (dt.order ?? 999) < (current.docType.order ?? 999))
    ) {
      result.set(dt.categoryType, { docType: dt, missingDeps });
    }
  }
  return result;
}

/** Counts, per category, how many of its active root document types haven't
 * been generated yet — the full remaining catalog, regardless of whether
 * each one is currently unblocked (unlike {@link buildBlockedByCategory} and
 * Next Steps, which only surface what can be generated right now). */
export function buildRemainingByCategory(
  docTypes: DocumentType[],
  existingDocumentTypes: Set<string>,
): Map<string, number> {
  const result = new Map<string, number>();
  for (const dt of docTypes) {
    if (dt.status !== 'Active' || dt.parentDocumentType !== null) continue;
    if (existingDocumentTypes.has(dt.type)) continue;
    result.set(dt.categoryType, (result.get(dt.categoryType) ?? 0) + 1);
  }
  return result;
}
